import { useVisits, type VisitDto } from "@repo/data-store";
import { EmptyState } from "@repo/ui/components/empty-state";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CalendarDays } from "lucide-react";
import { useMemo } from "react";
import { VisitListRow } from "@/features/visits";
import { GroupedList, type GroupedListGroup } from "@/shared/common/grouped-list";

export const Route = createFileRoute("/patients/$patientId/_layout/visits/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { patientId } = Route.useParams();
  const { visits, isLoading } = useVisits({ patientId });
  const navigate = useNavigate();

  const groups = useMemo<GroupedListGroup<VisitDto>[]>(() => {
    const now = new Date().toISOString();
    // Typed against the DTO's status, so a renamed rung fails to compile here
    // rather than quietly filing every visit under "past".
    const upcomingStatuses = new Set<VisitDto["status"]>(["booked", "proposed"]);
    const inProgressStatuses = new Set<VisitDto["status"]>(["checkedIn", "inProgress"]);
    const cancelledStatuses = new Set<VisitDto["status"]>(["cancelled", "noShow"]);

    const upcoming: VisitDto[] = [];
    const active: VisitDto[] = [];
    const past: VisitDto[] = [];
    const cancelled: VisitDto[] = [];

    for (const visit of visits) {
      const status = visit.status;
      const encStatus = visit.encounterStatus;

      if (cancelledStatuses.has(status)) {
        cancelled.push(visit);
      } else if (inProgressStatuses.has(status) || encStatus === "in-progress" || encStatus === "triaged") {
        active.push(visit);
      } else if (visit.start && visit.start >= now && upcomingStatuses.has(status)) {
        upcoming.push(visit);
      } else {
        past.push(visit);
      }
    }

    upcoming.sort((a, b) => (a.start ?? "").localeCompare(b.start ?? ""));
    active.sort((a, b) => (a.start ?? "").localeCompare(b.start ?? ""));
    past.sort((a, b) => (b.start ?? "").localeCompare(a.start ?? ""));
    cancelled.sort((a, b) => (b.start ?? "").localeCompare(a.start ?? ""));

    return [
      { id: "upcoming", name: "Appointments", items: [...active, ...upcoming] },
      { id: "history", name: "Visit History", items: past },
      { id: "cancelled", name: "Cancelled / No-Show", items: cancelled },
    ];
  }, [visits]);

  if (!isLoading && visits.length === 0) {
    return (
      <div className="flex flex-1 flex-col">
        <EmptyState className="m-auto">
          <EmptyState.Icon>
            <CalendarDays className="size-4" />
          </EmptyState.Icon>
          <EmptyState.Content>
            <EmptyState.Title>No visits yet</EmptyState.Title>
            <EmptyState.Description>Booked appointments and past visits will appear here.</EmptyState.Description>
          </EmptyState.Content>
        </EmptyState>
      </div>
    );
  }

  return (
    <GroupedList<VisitDto>
      groups={groups}
      isLoading={isLoading}
      getItemKey={(visit) => visit.visitId}
      emptyMessage="No visits found"
      groupEmptyMessage={(name) =>
        name === "Appointments"
          ? "No appointments"
          : name === "Cancelled / No-Show"
            ? "No cancelled or no-show appointments"
            : "No past visits"
      }
      renderItem={(visit) => (
        <VisitListRow
          visit={visit}
          patientId={patientId}
          onNavigate={(visitId) =>
            navigate({ to: "/patients/$patientId/visits/$visitId", params: { patientId, visitId } })
          }
        />
      )}
    />
  );
}
